import type { CSSProperties } from 'react';
import type { CharacterAppearance } from '../types';

export const BOOKWORM_OUTFITS = ['Default body', 'Professional', 'Creative', 'Cozy'] as const;
export const BOOKWORM_GLASSES = ['Off', 'On'] as const;

// Owner-approved full resolution transparent portraits. One illustration per
// outfit/glasses combination; never layer a generic shirt or prop over the worm.
const FRAME_BY_OUTFIT: Record<string, [number, number]> = {
  'Cozy': [0, 1],
  'Professional': [2, 3],
  'Default body': [4, 5],
  'Creative': [6, 7],
};
const ATLAS_URL = '/character-assets/bookworm-approved-clear-hires.avif';

// Eye positions are calibrated to each approved 900 x 1050 portrait, not
// the generic human/animal face anchors.
type Eye = { x: number; y: number; rx: number; ry: number };
const EYES: [Eye, Eye][] = [
  [{ x: 448, y: 325, rx: 61, ry: 61 }, { x: 590, y: 319, rx: 54, ry: 62 }],
  [{ x: 449, y: 326, rx: 59, ry: 61 }, { x: 591, y: 319, rx: 54, ry: 62 }],
  [{ x: 330, y: 262, rx: 68, ry: 67 }, { x: 528, y: 279, rx: 70, ry: 70 }],
  [{ x: 340, y: 255, rx: 67, ry: 72 }, { x: 528, y: 278, rx: 70, ry: 70 }],
  [{ x: 341, y: 256, rx: 63, ry: 69 }, { x: 526, y: 280, rx: 65, ry: 73 }],
  [{ x: 337, y: 251, rx: 65, ry: 71 }, { x: 524, y: 278, rx: 65, ry: 70 }],
  [{ x: 333, y: 258, rx: 64, ry: 69 }, { x: 520, y: 283, rx: 70, ry: 72 }],
  [{ x: 336, y: 251, rx: 62, ry: 70 }, { x: 523, y: 281, rx: 70, ry: 73 }],
];

export function bookwormOutfit(value?: string): string {
  return Object.prototype.hasOwnProperty.call(FRAME_BY_OUTFIT, value ?? '')
    ? (value as string)
    : 'Default body';
}

export function bookwormGlassesOn(value?: string): boolean {
  return value === 'On' ||
    value === 'Round glasses' ||
    value === 'Clear glasses' ||
    value === 'Smart glasses' ||
    value === 'Tinted glasses';
}

export function bookwormFrame(appearance: CharacterAppearance): number {
  const frames = FRAME_BY_OUTFIT[bookwormOutfit(appearance.outfit)];
  return frames[bookwormGlassesOn(appearance.eyewear) ? 0 : 1];
}

export default function BookwormApprovedArt({
  appearance,
}: {
  appearance: CharacterAppearance;
}) {
  const frame = bookwormFrame(appearance);
  const style: CSSProperties = {
    backgroundImage: `url("${ATLAS_URL}")`,
    backgroundRepeat: 'no-repeat',
    backgroundSize: '800% 100%',
    backgroundPosition: `${frame * 100 / 7}% 0%`,
  };
  const eyelidColor = frame < 2 ? '#9ada45' : '#9ce923';
  return (
    <div className="character-layer bookworm-approved-layer" data-bookworm-frame={frame} style={style} aria-hidden="true">
      <svg className="bookworm-blink-overlay" viewBox="0 0 900 1050" preserveAspectRatio="none">
        <g className="bookworm-eyelids" data-eyes="blinking">
          {EYES[frame].map((eye, index) => (
            <g key={index}>
              <ellipse cx={eye.x} cy={eye.y} rx={eye.rx} ry={eye.ry}
                fill={eyelidColor} stroke="none" />
              <path
                d={`M ${eye.x - eye.rx * 0.7} ${eye.y + 6} Q ${eye.x} ${eye.y + eye.ry * 0.34} ${eye.x + eye.rx * 0.7} ${eye.y + 6}`}
                fill="none" stroke="#315519" strokeWidth="8" strokeLinecap="round"
              />
            </g>
          ))}
        </g>
      </svg>
    </div>
  );
}
